import { SummaryRow } from "@/Modules/ModularConfigurator/options/summary/Summary";
import { FormValues, Item, MainProduct } from "@/types/productTypes";
import { calculateTotalPrice } from "@/utils/calculateTotalPrice";
import { produce } from "immer";
import { base64ToBlob } from "@/utils/imageUtils";
import { AdminSliceCreator, AdminStoreState } from "./useAdminStore";
import fetchDb from "@/utils/customRequestFunction";
import { z } from "zod";

// Zod schema for offer form validation
const offerSchema = z.object({
   name: z.string().min(2, "Name must be at least 2 characters"),
   surname: z.string().min(2, "Surname must be at least 2 characters"),
   email: z.string().email("Please enter a valid email address"),
   telephone: z.string().regex(/^\d+$/, "Telephone must contain only numbers"),
   postcode: z.string().min(3, "Postcode must be at least 3 characters"),
   country: z.string().min(2, "Country must be at least 2 characters"),
});

export type ConfiguratorStoreState = {
   formData: any;
   formErrors: Record<string, string>;
   pdfImages: {
      vidus: string | null;
      fasadas: string | null;
   };
   setPdfImages: (type: "vidus" | "fasadas", dataUrl: string) => void;
   handleSubmit: (
      allValues: FormValues,
      isIcelandic: boolean
   ) => Promise<Blob | null | undefined>;
   validateForm: () => { success: boolean; errors?: Record<string, string> };
   setFormData: (field: any, value: any) => void;
   validateField: (field: string, value: string) => void;
   clearFieldError: (field: string) => void;
   currentProduct: MainProduct | null | undefined;
   setCurrentProduct: (product: MainProduct | null | undefined) => void;
};

export const createMainConfiguratoreSlice: AdminSliceCreator<
   ConfiguratorStoreState
> = (set, get) => ({
   // ===== STATE =====
   activeStep: 0,
   threshold: 100,
   CONSTANTS: {},
   formData: {
      name: "",
      surname: "",
      email: "",
      telephone: "",
      postcode: "",
      country: "",
   },
   formErrors: {},
   pdfImages: {
      vidus: null,
      fasadas: null,
   },
   setPdfImages: (type: "vidus" | "fasadas", dataUrl: string) => {
      console.log("Setting PDF image for", type, dataUrl);
      set(
         produce((state: AdminStoreState) => {
            state.pdfImages[type] = dataUrl;
         })
      );
   },
   currentProduct: undefined,
   setCurrentProduct: (product) => {
      set(
         produce((state: AdminStoreState) => {
            state.currentProduct = product;
         })
      );
   },
   setFormData: (field, value) => {
      set(
         produce((state: AdminStoreState) => {
            state.formData[field] = value;
         })
      );
   },
   validateField: (field, value) => {
      try {
         // @ts-ignore - accessing shape properties dynamically
         offerSchema.shape[field].parse(value);
         set(
            produce((state: AdminStoreState) => {
               delete state.formErrors[field];
            })
         );
      } catch (err) {
         if (err instanceof z.ZodError) {
            set(
               produce((state: AdminStoreState) => {
                  state.formErrors[field] = err.errors[0].message;
               })
            );
         }
      }
   },
   clearFieldError: (field) => {
      set(
         produce((state: AdminStoreState) => {
            delete state.formErrors[field];
         })
      );
   },
   validateForm: () => {
      const formData = get().formData;
      const result = offerSchema.safeParse(formData);

      if (!result.success) {
         const newErrors: Record<string, string> = {};
         result.error.issues.forEach((issue) => {
            newErrors[issue.path[0].toString()] = issue.message;
         });

         set(
            produce((state: AdminStoreState) => {
               state.formErrors = newErrors;
            })
         );

         return { success: false, errors: newErrors };
      }

      // Clear errors on successful validation
      set(
         produce((state: AdminStoreState) => {
            state.formErrors = {};
         })
      );

      return { success: true };
   },
   handleSubmit: async (allValues, isIcelandic) => {
      try {
         const offerDetails = get().formData;
         const product = get().currentProduct;
         const compositeImages = get().pdfImages;
         console.log("compositeImages", compositeImages);

         // Convert Base64 to Blob
         const vidusImage = compositeImages.vidus
            ? base64ToBlob(compositeImages.vidus, "image/jpeg")
            : null;
         const fasadasImage = compositeImages.fasadas
            ? base64ToBlob(compositeImages.fasadas, "image/jpeg")
            : null;

         if (!product) return;
         console.log("offerDetailsNow", offerDetails);

         const pickedRows: SummaryRow[] = [];
         product.stages.forEach((stage) => {
            stage.options?.forEach((option) => {
               if (!option.id) return;
               const formValue = allValues[option.id.toString()];
               if (!formValue || !("selectedItem" in formValue)) return;
               const selected = formValue.selectedItem;
               if (Array.isArray(selected)) {
                  const foundItems = selected
                     .map((selId) =>
                        option.items?.find((it) => it.id?.toString() === selId)
                     )
                     .filter((item): item is Item => item !== undefined);
                  if (foundItems.length > 0) {
                     pickedRows.push({
                        type: "multiple",
                        option,
                        items: foundItems,
                     });
                  }
               } else {
                  const foundItem = option.items?.find(
                     (it) => it.id?.toString() === selected
                  );
                  if (foundItem) {
                     pickedRows.push({
                        type: "single",
                        option,
                        item: foundItem,
                     });
                  }
               }
            });
         });

         // 2. Minimal data
         const minimalRows = pickedRows.map((row) => ({
            type: row.type,
            option: {
               name: !isIcelandic ? row.option.name : row.option.islandicName,
               type: row.option.type,
            },
            items:
               row.type === "single"
                  ? {
                       name: !isIcelandic
                          ? row.item.name
                          : row.item.islandicName,
                       price: row.item.price,
                    }
                  : row.items.map((item) => ({
                       name: !isIcelandic ? item.name : item.islandicName,
                       price: item.price,
                    })),
         }));
         console.log("isIcelandic", isIcelandic);

         // 3. Calculate total price
         const price = calculateTotalPrice(
            allValues,
            product.stages,
            product.initialPrice
         );
         console.log("options", minimalRows);

         // 4. Create FormData for sending files
         const formData = new FormData();
         formData.append("productName", product.name);
         formData.append("totalPrice", price.toString());
         formData.append("options", JSON.stringify(minimalRows));
         formData.append("details", JSON.stringify(offerDetails));
         formData.append("isIcelandic", isIcelandic.toString());

         // Append images if they exist
         if (vidusImage) formData.append("vidus", vidusImage, "vidus.jpg");
         if (fasadasImage)
            formData.append("fasadas", fasadasImage, "fasadas.jpg");

         // 5. Post to your backend
         const response = await fetchDb.post("/api/pdf/sendEmail", formData);

         if (!response.ok) {
            console.error(
               "Failed to generate PDF or send email",
               await response.text()
            );
            return null;
         }

         return await response.blob();
      } catch (err) {
         console.error("Error submitting form:", err);
         return null;
      }
   },
});
